import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

function normalizeRow(row) {
  return {
    label: row?.label ?? row?.Parameter ?? row?.parameter ?? '',
    value: row?.value ?? row?.Value ?? '',
  };
}

function normalizeData(technicalData) {
  const headers = technicalData?.headers || ['Parameter', 'Value'];
  const raw = technicalData?.data ?? [];
  const data = raw.length > 0 ? raw.map(normalizeRow) : [{ label: '', value: '' }];
  return { headers, data };
}

/** Single table block: collapsible, default closed, editable rows + add/delete row. */
function EditableTableBlock({
  tableId,
  tableLabel,
  headers,
  data,
  defaultExpanded = false,
  onDataChange,
}) {
  const [open, setOpen] = useState(defaultExpanded);
  const rows = data.length > 0 ? data : [{ label: '', value: '' }];

  const handleCellChange = (rowIndex, field, value) => {
    const newData = rows.map((row, i) =>
      i === rowIndex ? { ...normalizeRow(row), [field]: value } : normalizeRow(row)
    );
    onDataChange?.({ headers, data: newData });
  };

  const handleAddRow = () => {
    onDataChange?.({ headers, data: [...rows.map(normalizeRow), { label: '', value: '' }] });
  };

  const handleDeleteRow = (rowIndex) => {
    if (rows.length <= 1) return;
    const newData = rows.filter((_, i) => i !== rowIndex).map(normalizeRow);
    onDataChange?.({ headers, data: newData });
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          py: 1,
          px: 1.5,
          bgcolor: 'grey.100',
          borderRadius: 1,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'grey.200' },
        }}
      >
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        <Typography variant="subtitle2" sx={{ fontSize: 13, fontWeight: 600 }}>
          {tableLabel}
        </Typography>
      </Box>
      <Collapse in={open} timeout={200}>
        <Box sx={{ pt: 1 }}>
          <TableContainer component={Paper} variant="outlined" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  {(headers || ['Parameter', 'Value']).map((h, i) => (
                    <TableCell key={i} sx={{ fontWeight: 600, fontSize: 13 }}>
                      {h}
                    </TableCell>
                  ))}
                  <TableCell sx={{ width: 48, fontWeight: 600 }} align="center" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    <TableCell sx={{ width: '40%', py: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        value={row.label}
                        onChange={(e) => handleCellChange(rowIndex, 'label', e.target.value)}
                        placeholder="Label"
                        sx={{ '& input': { fontSize: 13 } }}
                      />
                    </TableCell>
                    <TableCell sx={{ width: '60%', py: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        value={row.value}
                        onChange={(e) => handleCellChange(rowIndex, 'value', e.target.value)}
                        placeholder="Value"
                        sx={{ '& input': { fontSize: 13 } }}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 48, py: 0.5 }} align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteRow(rowIndex)}
                        disabled={rows.length <= 1}
                        aria-label="Delete row"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddRow}
            sx={{ mt: 1, textTransform: 'none', fontSize: 13 }}
          >
            Add New Row
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
}

export default function ProductCard({
  id,
  title,
  quantity,
  technicalData,
  uploadedImages = [],
  expanded: controlledExpanded,
  onExpandedChange,
  onQuantityChange,
  onTechnicalDataChange,
  onUploadedImagesChange,
  onDelete,
}) {
  const [internalExpanded, setInternalExpanded] = useState(false);

  const handleUploadChange = useCallback(
    (event) => {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;
      const newImages = files.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        url: URL.createObjectURL(file),
      }));
      onUploadedImagesChange?.([...(uploadedImages || []), ...newImages]);
      event.target.value = '';
    },
    [onUploadedImagesChange, uploadedImages]
  );

  const handleRemoveUploadedImage = useCallback(
    (imageId) => {
      const nextImages = (uploadedImages || []).filter((image) => {
        if (image.id === imageId && image.url?.startsWith('blob:')) {
          URL.revokeObjectURL(image.url);
        }
        return image.id !== imageId;
      });
      onUploadedImagesChange?.(nextImages);
    },
    [onUploadedImagesChange, uploadedImages]
  );
  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;
  const setExpanded = useCallback(
    (value) => {
      if (isControlled) onExpandedChange?.(value);
      else setInternalExpanded(value);
    },
    [isControlled, onExpandedChange]
  );

  const hasMultipleTables = technicalData?.tables && Array.isArray(technicalData.tables) && technicalData.tables.length > 0;
  const singleTable = hasMultipleTables ? null : normalizeData(technicalData);
  const { headers: singleHeaders, data: singleData } = singleTable || {};

  const handleSingleTableChange = useCallback(
    (next) => {
      onTechnicalDataChange?.(next);
    },
    [onTechnicalDataChange]
  );

  const handleMultiTableChange = useCallback(
    (tableIndex, next) => {
      const tables = [...(technicalData?.tables || [])];
      tables[tableIndex] = { ...tables[tableIndex], ...next };
      onTechnicalDataChange?.({ tables });
    },
    [technicalData?.tables, onTechnicalDataChange]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        mb: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            sx={{ transition: 'transform 0.2s ease' }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ flex: 1, fontSize: '0.9375rem' }}>
            {title || 'Product'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            type="number"
            label="Qty"
            value={quantity ?? 1}
            onChange={(e) => onQuantityChange?.(Math.max(1, parseInt(e.target.value, 10) || 1))}
            inputProps={{ min: 1, style: { width: 56 } }}
            sx={{ width: 80 }}
          />
          <IconButton size="small" color="error" onClick={onDelete} aria-label="Delete product">
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Collapse in={expanded} timeout={200} unmountOnExit>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
              Product images (uploaded images take display priority in quotation)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Button component="label" size="small" startIcon={<AddIcon />} sx={{ textTransform: 'none' }}>
                Upload image
                <input hidden accept="image/*" multiple type="file" onChange={handleUploadChange} />
              </Button>
              {(uploadedImages || []).length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {uploadedImages.map((image) => (
                    <Box
                      key={image.id}
                      sx={{
                        width: 92,
                        height: 92,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        position: 'relative',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <img
                        src={image.url}
                        alt={title || 'Uploaded product'}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveUploadedImage(image.id)}
                        sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.85)' }}
                        aria-label="Remove image"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              ) : null}
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: 12 }}>
            Technical data (editable)
          </Typography>
          {hasMultipleTables ? (
            technicalData.tables.map((tbl, idx) => (
              <EditableTableBlock
                key={tbl.id || idx}
                tableId={tbl.id}
                tableLabel={tbl.label || `Table ${idx + 1}`}
                headers={tbl.headers}
                data={tbl.data || []}
                defaultExpanded={false}
                onDataChange={(next) => handleMultiTableChange(idx, next)}
              />
            ))
          ) : (
            <EditableTableBlock
              tableId="single"
              tableLabel="Specification"
              headers={singleHeaders}
              data={singleData || []}
              defaultExpanded={false}
              onDataChange={handleSingleTableChange}
            />
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
